import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import ResponseBodyEditor from './ResponseBodyEditor';
import { BodyValidationError } from 'types/rest/bodyValidationTypes';
import { getEditorLanguage } from 'utils/bodyEditorUtil';
import { isJsonString } from 'utils/jsonUtil';
import { isXmlString } from 'utils/xmlUtil';
import axios from 'axios/axios-base';
import _ from 'lodash';
import BzmIcon from '../icons/BzmIcon';

interface ValueEditorModalProps {
  value: string;
  onSave: (value: string) => void;
  onClose: () => void;
}

const getContentType = (content: string): string => {
  if (!content) return 'text';
  if (isJsonString(content)) return 'json';
  if (isXmlString(content)) return 'xml';
  return 'text';
};

const ValueEditorModal: React.FC<ValueEditorModalProps> = ({ value, onSave, onClose }) => {
  const [localValue, setLocalValue] = useState(value);
  const [validationErrors, setValidationErrors] = useState<BodyValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);

  const contentType = getContentType(localValue);
  const language = getEditorLanguage(localValue, contentType);

  const handleValidate = () => {
    setIsValidating(true);
    setValidationErrors([]);
    setValidationSuccess(false);
    axios
      .post('/system/validations/handlebars/blazemeter', { template: localValue })
      .then(response => {
        const result = response.data.result;
        const errors: BodyValidationError[] = result.valid
          ? []
          : [
              {
                line: result.lineNumber ?? 1,
                column: 1,
                endLine: result.lineNumber ?? 1,
                endColumn: 1,
                message: result.message,
                severity: 'error' as const,
              },
            ];
        setValidationErrors(errors);
        setValidationSuccess(result.valid);
      })
      .catch(() => {
        setValidationErrors([
          {
            line: 1,
            column: 1,
            endLine: 1,
            endColumn: 1,
            message: 'Validation failed. Please try again.',
            severity: 'error' as const,
          },
        ]);
        setValidationSuccess(false);
      })
      .finally(() => {
        setIsValidating(false);
      });
  };

  const handleSave = () => {
    onSave(localValue);
    onClose();
  };

  return (
    <Modal show={true} onHide={onClose} centered={true}>
      <Modal.Header closeButton>
        <Modal.Title>Editor</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="font-weight-semi-bold">Value</span>
          <div className="d-flex align-items-center">
            {validationSuccess && <BzmIcon variant={'icon-bzm-success'} className="text-success mr-1" />}
            <button
              type="button"
              className="btn btn-link btn-no-hover p-0"
              onClick={handleValidate}
              disabled={isValidating}
            >
              <span className="font-size-base font-weight-semi-bold text-primary">
                {isValidating ? 'Validating...' : 'Validate'}
              </span>
            </button>
          </div>
        </div>
        <ResponseBodyEditor
          value={localValue}
          language={language}
          readOnly={false}
          height={300}
          validationErrors={validationErrors}
          onValidationErrorsDismiss={() => setValidationErrors([])}
          onChange={value => {
            setLocalValue(value);
            setValidationSuccess(false);
          }}
          onBlur={_.noop}
          isInvalid={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ValueEditorModal;
